import React, { useEffect, useRef } from 'react';

import { useTheme } from '@material-ui/core';

import { getColor } from '../../../theme/styles';
import {
  isLoaderMoving,
  LOADER_REST,
  loaderFrame,
  MARK_SETTLED_MS,
  MarkFrame,
  mixColours,
  MORPH_MS,
  morphFrame,
  PIECE_COUNT,
  piecePoints,
  REDUCED_FADE_MS,
  VIEW_BOX,
} from './geometry';

interface Props {
  /** The report is ready: finish the current loader step, then settle into the check. */
  done: boolean;
  size?: number;
  /** Fires once the check has landed (or faded in, with reduced motion): time to swap the copy. */
  onLanded?: () => void;
}

/**
 * The Vyce loader while a report generates, turning into a green 45° check built from the same
 * bars once it is ready. Drawn on requestAnimationFrame straight into the SVG, so React does not
 * re-render per frame. Design: claude.ai/artifact/2Zk2FFugXnXuUKsXFf3BNs
 */
export const ReportStatusMark: React.FC<Props> = ({ done, size = 96, onLanded }) => {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const from = theme.palette.primary.main;
  const to = getColor(isDark).pillOnText;

  const polygons = useRef<(SVGPolygonElement | null)[]>([]);
  const group = useRef<SVGGElement | null>(null);
  const ring = useRef<SVGCircleElement | null>(null);
  const doneRef = useRef(done);
  const coloursRef = useRef({ from, to });
  const onLandedRef = useRef(onLanded);
  const lastPaint = useRef<{ frame: MarkFrame; opacity: number } | null>(null);

  doneRef.current = done;
  onLandedRef.current = onLanded;

  const paint = (frame: MarkFrame, opacity = 1) => {
    lastPaint.current = { frame, opacity };
    const fill = mixColours(coloursRef.current.from, coloursRef.current.to, frame.colour);
    frame.pieces.forEach((piece, index) => {
      const polygon = polygons.current[index];
      if (!polygon) return;
      polygon.setAttribute('points', piecePoints(piece, frame));
      polygon.setAttribute('opacity', piece.o.toFixed(3));
      polygon.style.fill = fill;
    });
    group.current?.setAttribute('opacity', opacity.toFixed(3));
    if (ring.current) {
      ring.current.style.stroke = fill;
      ring.current.setAttribute('r', frame.ring ? frame.ring.r.toFixed(2) : '0');
      ring.current.setAttribute('opacity', frame.ring ? frame.ring.o.toFixed(3) : '0');
    }
  };

  // A theme switch repaints the frame on screen in the new colours
  useEffect(() => {
    coloursRef.current = { from, to };
    if (lastPaint.current) paint(lastPaint.current.frame, lastPaint.current.opacity);
  }, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let mode: 'loading' | 'morph' | 'settled' = 'loading';
    let landed = false;
    let raf = 0;
    const startedAt = performance.now();
    let morphStartedAt = 0;

    const land = () => {
      if (landed) return;
      landed = true;
      onLandedRef.current?.();
    };

    // Opened on a report that is already done: show the check, no replay
    if (doneRef.current) {
      paint(morphFrame(MARK_SETTLED_MS));
      land();
      return undefined;
    }

    const tick = (now: number) => {
      const elapsed = now - startedAt;

      if (mode === 'loading') {
        // Never cut a step short: wait for the hold between moves
        if (doneRef.current && (reduceMotion || !isLoaderMoving(elapsed))) {
          mode = 'morph';
          morphStartedAt = now;
        } else if (reduceMotion) {
          const pulse = 0.55 + 0.45 * (0.5 + 0.5 * Math.cos(elapsed / 500));
          paint({ ...loaderFrame(0), pieces: LOADER_REST }, pulse);
        } else {
          paint(loaderFrame(elapsed));
        }
      }

      if (mode === 'morph') {
        const sinceMorph = now - morphStartedAt;
        if (reduceMotion) {
          paint(morphFrame(MARK_SETTLED_MS), Math.min(1, sinceMorph / REDUCED_FADE_MS));
          if (sinceMorph >= REDUCED_FADE_MS) land();
        } else {
          paint(morphFrame(Math.min(sinceMorph, MARK_SETTLED_MS)));
          if (sinceMorph >= MORPH_MS) land();
        }
        if (sinceMorph >= MARK_SETTLED_MS) mode = 'settled';
      }

      if (mode !== 'settled') raf = requestAnimationFrame(tick);
    };

    paint(loaderFrame(0));
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const centre = VIEW_BOX / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`}
      role="img"
      aria-label={done ? 'Report ready' : 'Generating report'}
      style={{ overflow: 'visible', display: 'block' }}>
      <circle ref={ring} cx={centre} cy={centre} r={0} fill="none" strokeWidth={6} opacity={0} />
      <g ref={group}>
        {Array.from({ length: PIECE_COUNT }, (_, index) => (
          <polygon key={index} ref={element => (polygons.current[index] = element)} />
        ))}
      </g>
    </svg>
  );
};
